import {
  useMutation,
  UseMutationResult,
  useQuery,
} from "@tanstack/react-query";
import { queryKeys } from "../constants";
import { handleAPIError, queryClient } from "utils";
import { toast } from "react-toastify";
import {
  createStaff,
  deleteStaff,
  getStaff,
  getStaffById,
  updateStaff,
} from "services";
import { StaffType } from "types";

// Fetch all staff
export const useGetStaff = (id?: string | number) => {
  return useQuery({
    queryKey: [queryKeys.staff, id],
    queryFn: async () => await getStaff(id),
  });
};

// Fetch a single staff by ID
export const useGetStaffById = (id: string) => {
  return useQuery({
    queryKey: [queryKeys.staff, id],
    queryFn: async () => await getStaffById(id),
    enabled: !!id, // Only run query if
  });
};

// Create a new staff
export const useCreateStaff = () => {
  return useMutation({
    mutationFn: createStaff,
    onSuccess: async () => {
      toast.success("Staff created successfully.");

      // Invalidate staff query on success to refresh the list
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.staff],
      });
    },
    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};

// Define the UpdateStaffPayload type
export type UpdateStaffPayload = {
  id: string; // Required staff ID
  data: StaffType;
};

// Hook to update a staff
export const useUpdateStaff = (): UseMutationResult<
  any,
  Error,
  UpdateStaffPayload
> => {
  return useMutation<any, Error, UpdateStaffPayload>({
    mutationFn: async ({ id, data }) => {
      return updateStaff(id, data);
    },
    onSuccess: async () => {
      // Invalidate staff query on success
      toast.success("Staff updated successfully.");

      await queryClient.invalidateQueries({
        queryKey: [queryKeys.staff],
      });
    },
    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};

// Delete a staff
export const useDeleteStaff = () => {
  return useMutation({
    mutationFn: deleteStaff,
    onSuccess: async () => {
      toast.success("Staff deleted successfully.");

      // Invalidate staff query on success
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.staff],
      });
    },
    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};
